import { useMutation, useQuery } from '@apollo/client/react/index.js';
import {
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassConfirmDialog,
  GlassEmptyState,
  GlassInput,
  GlassSelect,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
} from '@merritrac/shared/components';
import { ArrowLeft, Building, Cpu, Save, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import {
  CREATE_PROPERTY,
  DELETE_PROPERTY,
  GET_CLIENTS,
  GET_PROPERTIES,
  GET_PROPERTY,
  UPDATE_PROPERTY,
} from '../lib/graphql/operations.js';

import type { ReactElement } from 'react';

/* ── GraphQL response types ── */

interface PropertyFloor {
  id: number;
  name: string;
  seq: number;
  units: {
    id: number;
    name: string;
    unitType: string | null;
    seq: number;
    locationTemplates: {
      id: number;
      name: string;
      seq: number;
    }[];
  }[];
}

interface PropertyData {
  property: {
    id: number;
    clientCode: string;
    name: string;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    type: string | null;
    totalUnits: number | null;
    notes: string | null;
    active: number | null;
    client: { clientCode: string; clientName: string | null };
    floors: PropertyFloor[];
  } | null;
}

interface ClientNode {
  clientCode: string;
  clientName: string | null;
}

interface GetClientsData {
  clients: {
    edges: { node: ClientNode }[];
  };
}

/* ── Form state ── */

interface PropertyForm {
  clientCode: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  type: string;
  totalUnits: string;
  notes: string;
}

const emptyForm = (presetClientCode: string): PropertyForm => ({
  clientCode: presetClientCode,
  name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  type: '',
  totalUnits: '',
  notes: '',
});

const PROPERTY_TYPES = [
  { value: '', label: 'Select type…' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'condo', label: 'Condo' },
  { value: 'office', label: 'Office' },
  { value: 'mixed_use', label: 'Mixed Use' },
  { value: 'single_family', label: 'Single Family' },
];

/* ── Page ── */

export function PropertyDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetClientCode = searchParams.get('clientCode') ?? '';

  const isNew = id === 'new' || id === undefined;
  const propertyId = isNew ? 0 : Number(id);

  const { data: propertyData, loading: propertyLoading } = useQuery<PropertyData>(GET_PROPERTY, {
    variables: { id: propertyId },
    skip: isNew,
  });

  const { data: clientsData } = useQuery<GetClientsData>(GET_CLIENTS, {
    variables: { first: 200 },
  });

  const existingProperty = propertyData?.property ?? null;

  const [form, setForm] = useState<PropertyForm>(emptyForm(presetClientCode));
  const [isDirty, setIsDirty] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [createProperty, { loading: creating }] = useMutation<{
    createProperty: { id: number; name: string; clientCode: string };
  }>(CREATE_PROPERTY, {
    refetchQueries: [{ query: GET_PROPERTIES, variables: { first: 200 } }],
  });
  const [updateProperty, { loading: updating }] = useMutation(UPDATE_PROPERTY, {
    refetchQueries: [
      { query: GET_PROPERTIES, variables: { first: 200 } },
      { query: GET_PROPERTY, variables: { id: propertyId } },
    ],
  });

  // Sync form when data loads
  useEffect(() => {
    if (existingProperty !== null) {
      setForm({
        clientCode: existingProperty.clientCode,
        name: existingProperty.name,
        address: existingProperty.address ?? '',
        city: existingProperty.city ?? '',
        state: existingProperty.state ?? '',
        zip: existingProperty.zip ?? '',
        type: existingProperty.type ?? '',
        totalUnits: existingProperty.totalUnits !== null ? String(existingProperty.totalUnits) : '',
        notes: existingProperty.notes ?? '',
      });
      setIsDirty(false);
    }
  }, [existingProperty?.id]);

  function handleChange(field: keyof PropertyForm, value: string): void {
    setForm((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  }

  function handleSave(e: React.SyntheticEvent): void {
    e.preventDefault();
    if (form.name.trim() === '' || form.clientCode.trim() === '') {
      return;
    }

    const totalUnitsInt =
      form.totalUnits !== '' ? parseInt(form.totalUnits, 10) || undefined : undefined;

    if (isNew) {
      void createProperty({
        variables: {
          clientCode: form.clientCode,
          name: form.name,
          address: form.address !== '' ? form.address : undefined,
          city: form.city !== '' ? form.city : undefined,
          state: form.state !== '' ? form.state : undefined,
          zip: form.zip !== '' ? form.zip : undefined,
          type: form.type !== '' ? form.type : undefined,
          totalUnits: totalUnitsInt,
          notes: form.notes !== '' ? form.notes : undefined,
        },
      }).then((result) => {
        const newId = result.data?.createProperty.id;
        if (newId !== undefined) {
          void navigate(`/properties/${String(newId)}`);
        } else {
          void navigate('/properties');
        }
      });
    } else {
      void updateProperty({
        variables: {
          id: propertyId,
          clientCode: form.clientCode !== '' ? form.clientCode : undefined,
          name: form.name !== '' ? form.name : undefined,
          address: form.address !== '' ? form.address : undefined,
          city: form.city !== '' ? form.city : undefined,
          state: form.state !== '' ? form.state : undefined,
          zip: form.zip !== '' ? form.zip : undefined,
          type: form.type !== '' ? form.type : undefined,
          totalUnits: totalUnitsInt,
          notes: form.notes !== '' ? form.notes : undefined,
        },
      }).then(() => {
        setIsDirty(false);
      });
    }
  }

  function handleBack(): void {
    void navigate('/properties');
  }

  const saving = creating || updating;
  const loading = !isNew && propertyLoading;

  const clientOptions = [
    { value: '', label: 'Select client…' },
    ...(clientsData?.clients.edges.map((e) => ({
      value: e.node.clientCode,
      label:
        e.node.clientName !== null && e.node.clientName !== ''
          ? `${e.node.clientName} (${e.node.clientCode})`
          : e.node.clientCode,
    })) ?? []),
  ];

  const floors = existingProperty?.floors ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <GlassButton
            variant="ghost"
            size="sm"
            onClick={handleBack}
            aria-label="Back to properties"
          >
            <ArrowLeft size={14} />
          </GlassButton>
          <div>
            <h1 className="text-xl font-semibold text-text">
              {isNew
                ? 'New Property'
                : form.name !== ''
                  ? form.name
                  : `Property #${String(propertyId)}`}
            </h1>
            {!isNew && (
              <p className="text-sm text-text-secondary mt-0.5">
                Property ID: {propertyId}
                {existingProperty?.client.clientName !== null &&
                existingProperty?.client.clientName !== undefined &&
                existingProperty.client.clientName !== ''
                  ? ` · ${existingProperty.client.clientName}`
                  : ''}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <GlassButton
              variant="ghost"
              size="sm"
              className="text-danger hover:text-danger hover:bg-danger-soft"
              onClick={() => {
                setShowDelete(true);
              }}
            >
              <Trash2 size={14} />
              Delete
            </GlassButton>
          )}
          <GlassButton
            variant="primary"
            size="md"
            onClick={handleSave}
            disabled={saving || !isDirty}
          >
            <Save size={14} />
            {saving ? 'Saving…' : 'Save'}
          </GlassButton>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-text-secondary">Loading…</div>
      ) : (
        <form onSubmit={handleSave}>
          {/* Property Information */}
          <GlassCard>
            <GlassCardHeader>
              <h2 className="text-base font-semibold text-text">Property Information</h2>
            </GlassCardHeader>
            <GlassCardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <GlassSelect
                  label="Client"
                  value={form.clientCode}
                  onChange={(e) => {
                    handleChange('clientCode', e.target.value);
                  }}
                  options={clientOptions}
                  required
                />
                <GlassInput
                  label="Property Name"
                  value={form.name}
                  onChange={(e) => {
                    handleChange('name', e.target.value);
                  }}
                  placeholder="e.g. Oakwood Apartments"
                  required
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <GlassSelect
                  label="Property Type"
                  value={form.type}
                  onChange={(e) => {
                    handleChange('type', e.target.value);
                  }}
                  options={PROPERTY_TYPES}
                />
                <GlassInput
                  label="Total Units"
                  type="number"
                  value={form.totalUnits}
                  onChange={(e) => {
                    handleChange('totalUnits', e.target.value);
                  }}
                  placeholder="e.g. 48"
                  min={0}
                />
              </div>
            </GlassCardContent>
          </GlassCard>

          {/* Address */}
          <GlassCard className="mt-6">
            <GlassCardHeader>
              <h2 className="text-base font-semibold text-text">Address</h2>
            </GlassCardHeader>
            <GlassCardContent className="space-y-4">
              <GlassInput
                label="Street Address"
                value={form.address}
                onChange={(e) => {
                  handleChange('address', e.target.value);
                }}
                placeholder="123 Main St"
              />
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <GlassInput
                  label="City"
                  value={form.city}
                  onChange={(e) => {
                    handleChange('city', e.target.value);
                  }}
                  placeholder="City"
                />
                <GlassInput
                  label="State"
                  value={form.state}
                  onChange={(e) => {
                    handleChange('state', e.target.value);
                  }}
                  placeholder="ST"
                />
                <GlassInput
                  label="ZIP Code"
                  value={form.zip}
                  onChange={(e) => {
                    handleChange('zip', e.target.value);
                  }}
                  placeholder="12345"
                />
              </div>
            </GlassCardContent>
          </GlassCard>

          {/* Notes */}
          <GlassCard className="mt-6">
            <GlassCardHeader>
              <h2 className="text-base font-semibold text-text">Notes</h2>
            </GlassCardHeader>
            <GlassCardContent>
              <textarea
                value={form.notes}
                onChange={(e) => {
                  handleChange('notes', e.target.value);
                }}
                rows={4}
                placeholder="Additional notes about this property…"
                className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y"
              />
            </GlassCardContent>
          </GlassCard>

          {/* Property Hierarchy: Floors → Units → Locations */}
          {!isNew && (
            <GlassCard className="mt-6">
              <GlassCardHeader>
                <h2 className="text-base font-semibold text-text">Building Hierarchy</h2>
              </GlassCardHeader>
              <GlassCardContent>
                {floors.length === 0 ? (
                  <GlassEmptyState
                    icon={Cpu}
                    title="No floors configured"
                    description="Floors, units, and locations for this property will appear here."
                  />
                ) : (
                  <GlassTable>
                    <GlassTableHead>
                      <GlassTableRow>
                        <GlassTableHeaderCell>Floor</GlassTableHeaderCell>
                        <GlassTableHeaderCell>Unit</GlassTableHeaderCell>
                        <GlassTableHeaderCell>Locations</GlassTableHeaderCell>
                      </GlassTableRow>
                    </GlassTableHead>
                    <GlassTableBody>
                      {floors.map((floor) =>
                        floor.units.length === 0 ? (
                          <GlassTableRow key={floor.id}>
                            <GlassTableCell>
                              <div className="flex items-center gap-2">
                                <Building size={14} className="text-text-secondary shrink-0" />
                                <span className="font-medium text-text">Floor {floor.name}</span>
                              </div>
                            </GlassTableCell>
                            <GlassTableCell className="text-text-secondary">—</GlassTableCell>
                            <GlassTableCell className="text-text-secondary">—</GlassTableCell>
                          </GlassTableRow>
                        ) : (
                          floor.units.map((unit, unitIdx) => (
                            <GlassTableRow key={`${String(floor.id)}-${String(unit.id)}`}>
                              {unitIdx === 0 ? (
                                <GlassTableCell>
                                  <div className="flex items-center gap-2">
                                    <Building size={14} className="text-text-secondary shrink-0" />
                                    <span className="font-medium text-text">
                                      Floor {floor.name}
                                    </span>
                                    <GlassBadge variant="default">
                                      {floor.units.length} units
                                    </GlassBadge>
                                  </div>
                                </GlassTableCell>
                              ) : (
                                <GlassTableCell>{''}</GlassTableCell>
                              )}
                              <GlassTableCell>
                                <span className="font-medium text-text">{unit.name}</span>
                                {unit.unitType !== null && (
                                  <span className="ml-2 text-xs text-text-secondary">
                                    {unit.unitType}
                                  </span>
                                )}
                              </GlassTableCell>
                              <GlassTableCell>
                                <span className="text-sm text-text-secondary">
                                  {unit.locationTemplates.map((loc) => loc.name).join(', ') || '—'}
                                </span>
                              </GlassTableCell>
                            </GlassTableRow>
                          ))
                        ),
                      )}
                    </GlassTableBody>
                  </GlassTable>
                )}
              </GlassCardContent>
            </GlassCard>
          )}
        </form>
      )}

      {/* Delete confirmation dialog */}
      {!isNew && (
        <DeletePropertyConfirm
          propertyName={form.name !== '' ? form.name : `Property #${String(propertyId)}`}
          propertyId={propertyId}
          open={showDelete}
          onClose={() => {
            setShowDelete(false);
          }}
          onDeleted={() => {
            void navigate('/properties');
          }}
        />
      )}
    </div>
  );
}

/* ── Delete Confirm ── */

function DeletePropertyConfirm({
  propertyName,
  propertyId,
  open,
  onClose,
  onDeleted,
}: {
  propertyName: string;
  propertyId: number;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}): ReactElement {
  const [deleteProperty, { loading }] = useMutation(DELETE_PROPERTY, {
    refetchQueries: [{ query: GET_PROPERTIES, variables: { first: 200 } }],
  });

  return (
    <GlassConfirmDialog
      open={open}
      title="Delete Property"
      message={`Are you sure you want to delete "${propertyName}"? This will permanently remove the property and cannot be undone.`}
      confirmLabel="Delete Property"
      variant="danger"
      loading={loading}
      onCancel={onClose}
      onConfirm={() => {
        void deleteProperty({ variables: { id: propertyId } }).then(() => {
          onDeleted();
        });
      }}
    />
  );
}
